import { Button } from '../../../../components/ui/button';
import { confirmationSubtitle, noAddressSkipLine, noEmailWaitMessage, suppressedSkipLine, willWaitLine } from './bulkSendCopy';
import { EmailRow, RecipientAvatars, WarningRow, type SummaryPerson } from './sendSummaryParts';

/** The wizard names its result region by whichever heading is showing (this, or the confirmation's). */
export const RESULT_TITLE_ID = 'bulk-send-result-title';
export const RESULT_DESC_ID = 'bulk-send-result-desc';

/** P40 (owner): how long a send may run with no error before the admin is told it carries on without them. */
export const STILL_SENDING_AFTER_MS = 60_000;

export interface SendingViewProps {
  /** Emails that can go out in this send (ready drafts only); 0 while unknown hides the subtitle and the Email row. */
  count?: number;
  /** Drafts approved for a creator with no email yet. They are never counted in `count`; with nothing ready the view reads Approving. */
  waiting?: number;
  people?: SummaryPerson[];
  /** Set for a scheduled send: the title reads "Scheduling" and the subtitle names the time. */
  scheduledAt?: string;
  /** Creators already known to be left out (Channel drops and Send-time addressless). */
  noAddress?: number;
  suppressed?: number;
  /** The approve's progress can't be read: the emails still go out, so say so and let the admin leave. */
  progressError?: boolean;
  /** P40: the send has run past STILL_SENDING_AFTER_MS with no error. */
  stillSending?: boolean;
  /** Shown as a Close button when set — the wizard passes it once the admin may leave. */
  onClose?: () => void;
}

/** P32 (Figma 665:96219): loader ring, "Sending started" ("Scheduling" for a scheduled send), the subtitle, recipients and the Email breakdown while the approve runs. */
export function SendingView({ count = 0, waiting = 0, people = [], scheduledAt, noAddress = 0, suppressed = 0, progressError = false, stillSending = false, onClose }: SendingViewProps) {
  // Status honesty (owner 2026-10-15): with nothing ready, no mail is going out, so this is never "Sending started".
  const nothingReady = count === 0 && waiting > 0;
  const warnings = [noAddress > 0 ? noAddressSkipLine(noAddress) : null, count > 0 && waiting > 0 ? willWaitLine(waiting) : null, suppressed > 0 ? suppressedSkipLine(suppressed) : null].filter((line): line is string => line !== null);
  const hasSummary = count > 0 || warnings.length > 0;
  return (
    <div className="m-auto flex w-full max-w-[520px] flex-col items-center gap-4 text-center">
      <span
        aria-hidden="true"
        className="size-12 animate-spin rounded-full border-[3px] border-[#e5e5e5] border-t-black motion-reduce:animate-none"
      />
      <div className="space-y-2">
        <h2 id={RESULT_TITLE_ID} className="text-2xl font-medium text-[#0a0a0a]">{nothingReady ? 'Approving' : scheduledAt ? 'Scheduling' : 'Sending started'}</h2>
        {nothingReady ? (
          <p id={RESULT_DESC_ID} className="text-base text-muted-foreground">{noEmailWaitMessage(waiting)}</p>
        ) : count > 0 ? (
          <p id={RESULT_DESC_ID} className="text-base text-muted-foreground">{confirmationSubtitle({ count, scheduledAt })}</p>
        ) : null}
      </div>
      <RecipientAvatars people={people} />
      {hasSummary ? (
        <ul role="list" aria-label="Send summary" className="w-full overflow-hidden rounded-xl border border-border bg-white">
          {count > 0 ? <EmailRow count={count} /> : null}
          {warnings.map((line) => <WarningRow key={line}>{line}</WarningRow>)}
        </ul>
      ) : null}
      {progressError ? (
        <p role="alert" className="text-body-sm-regular text-rose-700">
          {nothingReady ? "Couldn't read the approval progress — open each creator's row to see where it stands." : "Couldn't read the send progress — the approved emails still go out. Check the Scheduled tab."}
        </p>
      ) : null}
      {/* Always rendered (never display:none) so screen readers announce the note when it appears; empty, it cancels the gap. */}
      <p role="status" className="text-body-sm-regular text-muted-foreground empty:-mt-4">{stillSending && !progressError ? (nothingReady ? "Still approving in the background — you can close this; each creator's row shows the result." : 'Still sending in the background — you can close this; results appear in the Inbox.') : null}</p>
      {onClose ? <Button variant="outline" className="h-10 rounded-full px-5" onClick={onClose}>Close</Button> : null}
    </div>
  );
}
